import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { EncounterChip } from './EncounterChip';
import { Skeleton } from '@/components/ui/skeleton';
import { useImagingQuery } from '@/features/imaging';
import { formatCalendarDate } from '@/lib/utils/date';
import { type CalendarEntry, type CalendarWindow, useCalendar } from '../api/calendar-api';

/** The day query is separate from the month query so a capped month cannot hide appointments here. */
export function DaySchedule({
  date,
  onClose,
  assignedTo,
  canReadImaging,
}: {
  date: string;
  assignedTo?: string;
  canReadImaging: boolean;
  onClose: () => void;
}) {
  const calendar = useCalendar({ date_to: date, date_from: date, ...(assignedTo ? { assigned_to: assignedTo } : {}) });
  const imaging = useImagingQuery<CalendarWindow>(
    `/calendar?date_from=${date}&date_to=${date}`,
    canReadImaging && !assignedTo,
  );
  const entries = [...(calendar.data?.entries ?? []), ...(!assignedTo ? imaging.data?.entries ?? [] : [])]
    .filter((entry) => entry.encounter_date === date)
    .sort((a, b) => (a.scheduled_local_time ?? '99:99').localeCompare(b.scheduled_local_time ?? '99:99'));
  const byHour = new Map<number, CalendarEntry[]>();
  const unscheduled: CalendarEntry[] = [];
  for (const entry of entries) {
    const hour = entry.scheduled_local_time ? Number(entry.scheduled_local_time.slice(0, 2)) : NaN;
    if (!Number.isInteger(hour) || hour < 0 || hour > 23) unscheduled.push(entry);
    else byHour.set(hour, [...(byHour.get(hour) ?? []), entry]);
  }
  const scheduledHours = [...byHour.keys()];
  const firstHour = Math.min(7, ...scheduledHours);
  const lastHour = Math.max(19, ...scheduledHours);

  return (
    <section aria-label={`Schedule for ${date}`} className="min-w-0 rounded-[18px] border border-border bg-card">
      <div className="sticky top-0 z-10 flex items-start justify-between gap-2 border-b bg-card p-4">
        <div>
          <h2 className="text-lg font-semibold">{formatCalendarDate(date, 'EEEE, MMMM d, yyyy')}</h2>
          <p className="text-xs text-muted-foreground">{entries.length} scheduled {entries.length === 1 ? 'entry' : 'entries'} · practice time</p>
        </div>
        <Button size="icon" type="button" variant="ghost" aria-label="Close daily schedule" onClick={onClose}>
          <X className="size-4" />
        </Button>
      </div>

      {(calendar.data?.truncated || imaging.data?.truncated) && (
        <p role="alert" className="mx-4 mt-4 rounded-md bg-warning-surface p-3 text-sm text-warning">
          This day has more entries than the calendar can load. Filter by clinician to see more.
        </p>
      )}
      {(calendar.isError || (canReadImaging && !assignedTo && imaging.isError)) && (
        <p role="alert" className="mx-4 mt-4 text-sm text-destructive">Some appointments could not be loaded.</p>
      )}
      {calendar.isLoading || (canReadImaging && !assignedTo && imaging.isLoading) ? (
        <Skeleton className="m-4 h-96" />
      ) : (
        <div className="max-h-[640px] overflow-y-auto p-4">
          {entries.length === 0 && !calendar.isError && <p className="mb-3 text-sm text-muted-foreground">Nothing scheduled for this date.</p>}
          {Array.from({ length: lastHour - firstHour + 1 }, (_, index) => firstHour + index).map((hour) => (
            <div key={hour} className="grid min-h-12 grid-cols-[68px_minmax(0,1fr)] gap-2 border-t py-2 first:border-t-0">
              <span className="pt-1 text-xs tabular-nums text-muted-foreground">
                {new Intl.DateTimeFormat('en-US', { hour: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(2020, 0, 1, hour)))}
              </span>
              <div className="flex flex-col gap-1">
                {(byHour.get(hour) ?? []).map((entry) => <EncounterChip key={entry.id} entry={entry} className="py-2 text-sm" />)}
              </div>
            </div>
          ))}
          {unscheduled.length > 0 && (
            <div className="grid grid-cols-[68px_minmax(0,1fr)] gap-2 border-t py-2">
              <span className="pt-1 text-xs text-muted-foreground">No time</span>
              <div className="flex flex-col gap-1">
                {unscheduled.map((entry) => <EncounterChip key={entry.id} entry={entry} className="py-2 text-sm" />)}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
